import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Coins, ExternalLink, MailWarning, CalendarCheck, TrendingUp, AppWindow } from "lucide-react";

const PROFILE_BADGE = {
  a_valider: "bg-amber-100 text-amber-900 border-amber-300",
  free: "bg-slate-100 text-slate-800 border-slate-300",
  premium: "bg-amber-50 text-amber-800 border-amber-300",
  team: "bg-teal-100 text-teal-900 border-teal-300",
};
const PROFILE_LABEL = { a_valider: "À valider", free: "Free", premium: "Premium", team: "Team" };

const APP_META = {
  simulation: {
    icon: TrendingUp,
    description: "Entraîne-toi sur des simulations immobilières réalistes.",
    image: "/assets/simulation.png",
  },
  agenda: {
    icon: CalendarCheck,
    description: "Organise ta semaine et planifie tes rendez-vous.",
    image: "/assets/agenda.png",
  },
};

export default function DashboardPage() {
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [unverified, setUnverified] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendMsg, setResendMsg] = useState("");
  const [creditDialog, setCreditDialog] = useState(null); // {app, result}

  const load = () => {
    api
      .get("/dashboard")
      .then((r) => setData(r.data))
      .catch((err) => {
        if (err?.response?.data?.detail === "email_non_verifie") setUnverified(true);
        else toast.error("Impossible de charger le tableau de bord.");
      });
  };

  useEffect(load, [user?.verification_status]);

  // Tant que le compte est en attente/refusé, re-vérifier le statut en base (validation admin en temps réel)
  useEffect(() => {
    if (!user || !user.email_verified || user.verification_status === "approved") return;
    const interval = setInterval(() => {
      refresh().catch(() => {});
    }, 8000);
    return () => clearInterval(interval);
  }, [user, refresh]);

  const handleOpen = (app) => {
    api
      .post("/launch", { app_id: app.app_id })
      .then(({ data: r }) => window.open(`${r.url}/auth/portal?code=${r.launch_code}`, "_blank", "noopener"))
      .catch((err) => {
        toast.info(err?.response?.data?.detail || "Ouverture impossible pour le moment.", { id: `open-${app.app_id}` });
      });
  };

  const handleCredits = (app) => {
    if (data.user.profile === "a_valider") {
      setCreditDialog({
        app,
        result: {
          action: "message",
          message:
            "Ton compte est encore en cours de validation. Tes crédits de bienvenue sont déjà disponibles : tu peux déjà t'entraîner et commencer à organiser ta semaine.",
        },
      });
      return;
    }
    api
      .post("/credits/request", { app_id: app.app_id })
      .then(({ data: result }) => {
        if (result.action === "email_sent" || result.action === "already_pending") {
          setData((d) => ({
            ...d,
            apps: d.apps.map((a) => (a.app_id === app.app_id ? { ...a, pending_request: true } : a)),
          }));
        }
        setCreditDialog({ app, result });
      })
      .catch(() => toast.error("Demande impossible pour le moment."));
  };

  const handleResend = async (e) => {
    e.preventDefault();
    try {
      const { data: r } = await api.post("/auth/resend-verification", { email: resendEmail || user.email });
      setResendMsg(r.message);
    } catch {
      setResendMsg("Une erreur est survenue.");
    }
  };

  if (user && user.email_verified && user.verification_status === "pending") {
    return (
      <Layout>
        <div className="max-w-lg mx-auto bg-white border border-amber-300 rounded-xl p-8 text-center shadow-sm" data-testid="pending-screen">
          <MailWarning className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-2">Inscription en cours de vérification</h2>
          <p className="text-sm text-slate-600 mb-6">
            Ton inscription a bien été enregistrée. Pendant que nous vérifions ton accès, tu peux visiter ou
            t'inscrire à la communauté Skool gratuite et indépendante dédiée aux agents et conseillers immobiliers.
            Tu recevras un e-mail dès que ton accès aux applications Mindset'iMMO aura été validé.
          </p>
          <a href="https://www.skool.com/dominique-piredda" target="_blank" rel="noopener noreferrer" data-testid="pending-skool-link">
            <Button className="bg-amber-500 hover:bg-amber-600">Visiter ou rejoindre la communauté Skool</Button>
          </a>
        </div>
      </Layout>
    );
  }

  if (user && user.email_verified && user.verification_status === "rejected") {
    return (
      <Layout>
        <div className="max-w-lg mx-auto bg-white border border-slate-300 rounded-xl p-8 text-center shadow-sm" data-testid="rejected-screen">
          <MailWarning className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-2">Accès non validé</h2>
          <p className="text-sm text-slate-600 mb-6">
            Ton accès aux applications n'a pas été validé pour le moment. Les applications Mindset'iMMO sont
            réservées aux membres de la communauté Skool : rejoins-la gratuitement ou contacte Dominique si tu
            penses qu'il s'agit d'une erreur.
          </p>
          <a href="https://www.skool.com/dominique-piredda" target="_blank" rel="noopener noreferrer" data-testid="rejected-skool-link">
            <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50">Rejoindre la communauté Skool</Button>
          </a>
        </div>
      </Layout>
    );
  }

  if (unverified) {
    return (
      <Layout>
        <div className="max-w-lg mx-auto bg-white border border-amber-300 rounded-xl p-8 text-center shadow-sm" data-testid="unverified-banner">
          <MailWarning className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-2">Vérifie ton adresse email</h2>
          <p className="text-sm text-slate-600 mb-6">
            Ton compte n'est pas encore activé : tant que ton email n'est pas vérifié, tu n'as pas accès aux
            applications ni aux crédits. Clique sur le lien reçu par email (valable 24 h).
          </p>
          <form onSubmit={handleResend} className="space-y-3 text-left">
            <input
              type="email"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              placeholder={user.email}
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              data-testid="unverified-resend-email-input"
            />
            <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-600" data-testid="unverified-resend-button">
              Renvoyer le lien de vérification
            </Button>
            {resendMsg && <p className="text-sm text-teal-700" data-testid="unverified-resend-message">{resendMsg}</p>}
          </form>
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="text-center text-slate-500 py-20" data-testid="dashboard-loading">Chargement...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900" data-testid="dashboard-title">
            Bonjour {data.user.prenom}
          </h1>
          <p className="text-slate-600 mt-1">Voici tes applications et tes soldes de crédits.</p>
        </div>
        <Badge variant="outline" className={`${PROFILE_BADGE[data.user.profile]} font-semibold px-3 py-1 w-fit`} data-testid="profile-badge">
          {PROFILE_LABEL[data.user.profile]}
        </Badge>
      </div>

      {data.user.profile === "a_valider" && (
        <div className="bg-amber-50 border-2 border-amber-400 text-amber-900 rounded-xl p-5 mb-8 shadow-sm" data-testid="a-valider-message">
          Ton compte est en cours de validation. Tes crédits de bienvenue sont déjà disponibles : tu peux déjà
          t'entraîner et commencer à organiser ta semaine.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="apps-grid">
        {data.apps.map((app) => {
          const meta = APP_META[app.app_id] || { icon: AppWindow, description: "Application du portail.", image: null };
          const Icon = meta.icon;
          return (
            <div
              key={app.app_id}
              className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-teal-500/40 transition-all duration-200 overflow-hidden flex flex-col"
              data-testid={`app-card-${app.app_id}`}
            >
              {meta.image && (
                <div className="h-36 overflow-hidden">
                  <img src={meta.image} alt={app.nom} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-6 flex flex-col gap-4 flex-1">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-teal-700" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-slate-800">{app.nom}</h3>
                      <p className="text-sm text-slate-500">{meta.description}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-auto">
                  {app.access ? (
                    <div className="flex items-center gap-2" data-testid={`balance-${app.app_id}`}>
                      <Coins className="w-4 h-4 text-amber-500" />
                      <span className="text-sm text-slate-600">Solde :</span>
                      <span className="font-bold text-slate-900">{data.user.role === "admin" ? "Illimité" : (app.balance ?? 0)}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-slate-500 italic" data-testid={`no-access-${app.app_id}`}>Accès non activé</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    className="flex-1 bg-teal-600 hover:bg-teal-700"
                    disabled={!app.access}
                    onClick={() => handleOpen(app)}
                    data-testid={`open-app-${app.app_id}`}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" /> Ouvrir
                  </Button>
                  {app.access && app.balance !== null && (
                    data.user.profile === "free" ? (
                      <Button
                        variant="outline"
                        className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                        onClick={() => setCreditDialog({ app, title: `Acheter des crédits — ${app.nom}`, result: { message: "L'achat de crédits sera bientôt disponible." } })}
                        data-testid={`buy-credits-${app.app_id}`}
                      >
                        Acheter des crédits
                      </Button>
                    ) : app.pending_request ? (
                      <Button variant="outline" className="flex-1 border-teal-200 text-teal-700 bg-teal-50" disabled data-testid={`demande-en-cours-${app.app_id}`}>
                        Demande en cours
                      </Button>
                    ) : (
                      <Button variant="outline" className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => handleCredits(app)} data-testid={`get-credits-${app.app_id}`}>
                        Obtenir des crédits
                      </Button>
                    )
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!creditDialog} onOpenChange={() => setCreditDialog(null)}>
        <DialogContent data-testid="credit-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{creditDialog?.title || `Obtenir des crédits — ${creditDialog?.app?.nom}`}</DialogTitle>
            <DialogDescription asChild>
              <p className="text-slate-600 pt-2" data-testid="credit-request-message">{creditDialog?.result?.message}</p>
            </DialogDescription>
          </DialogHeader>
          <Button onClick={() => setCreditDialog(null)} className="bg-teal-600 hover:bg-teal-700" data-testid="credit-dialog-close">
            Fermer
          </Button>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
